/**
 * CteAddPopover — `[+ add CTE]` form popover. Unlike the column-chooser style
 * popovers in sibling slices, this one surfaces a small two-field form (name
 * + body) since adding a CTE requires user-typed text rather than picking
 * from a suggestion list. Submitting commits an `addCte` op.
 *
 * Without `editContext` the popover renders as a disabled VQE-RO `[+ add CTE]`
 * button (mirrors the GroupBy / OrderBy / FromAddTable scaffolding contract).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.
 */

import { useId, useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { VqeTooltip } from '../common/VqeTooltip';
import { addCte } from '../../edit/edits/addCte';
import { vqeCopy } from '../../ux/vqeCopy';

export interface CteAddPopoverProps {
  readonly editContext?: VqeEditContext;
  /**
   * Controlled open state. When provided, the popover is driven externally
   * (e.g. by {@link CteAddMenu}'s "Add CTE" item) rather than self-managing
   * `open` via its own trigger. Omit for the default uncontrolled behaviour.
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /**
   * Hide the `[+ add CTE]` trigger button and anchor the form to a zero-size
   * `<PopoverAnchor>` instead. Used by {@link CteAddMenu} so the kebab menu
   * item — not a separate Plus button — opens the form. Requires controlled
   * `open` / `onOpenChange`.
   */
  readonly hideTrigger?: boolean;
}

export function CteAddPopover({ editContext, open, onOpenChange, hideTrigger }: CteAddPopoverProps) {
  if (!editContext) {
    return (
      <VqeTooltip content={vqeCopy.disabled.editingUnavailable}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          disabled
          aria-disabled
          data-testid="vqe-cte-add"
        >
          <Plus className="h-3.5 w-3.5" />
          {vqeCopy.actions.addCte}
        </Button>
      </VqeTooltip>
    );
  }
  return (
    <CteAddPopoverEnabled
      editContext={editContext}
      controlledOpen={open}
      onOpenChange={onOpenChange}
      hideTrigger={hideTrigger}
    />
  );
}

function CteAddPopoverEnabled({
  editContext,
  controlledOpen,
  onOpenChange,
  hideTrigger,
}: {
  editContext: VqeEditContext;
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}) {
  // Support both controlled (driven by CteAddMenu) and the original
  // uncontrolled (self-managed trigger) modes. Existing callers pass neither
  // `controlledOpen` nor `hideTrigger`, so their behaviour is byte-identical.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean): void => {
    if (isControlled) onOpenChange?.(next);
    else setUncontrolledOpen(next);
  };
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const nameId = useId();
  const bodyId = useId();

  const reset = (): void => {
    setName('');
    setBody('');
  };

  const submit = async (): Promise<void> => {
    if (name.trim().length === 0 || body.trim().length === 0) return;
    try {
      await addCte(name.trim(), body.trim(), toCommitCtx(editContext));
      reset();
      setOpen(false);
    } catch {
      // Validation errors surface in the form; keep the popover open.
    }
  };

  return (
    <Popover open={open} onOpenChange={(next) => { if (!next) reset(); setOpen(next); }}>
      {hideTrigger ? (
        // Zero-size anchor: the form is opened by an external control (the
        // kebab Add-CTE menu), so the Plus trigger button is suppressed.
        <PopoverAnchor className="h-0 w-0" />
      ) : (
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
            data-testid="vqe-cte-add"
            aria-label="Add CTE"
          >
            <Plus className="h-3.5 w-3.5" />
            {vqeCopy.actions.addCte}
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent
        className="w-80 space-y-2"
        data-testid="vqe-cte-add-popover"
        // Don't auto-dismiss on a focus shift — a late schema-refresh re-render
        // over the Monaco editor can otherwise tear this open form down
        // mid-edit. Mirrors FilterBuilder / CompactVqePopover; outside
        // pointer-down still dismisses.
        onFocusOutside={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={nameId} className="text-xs font-medium">
            Name
          </label>
          <input
            id={nameId}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="vqe-cte-add-name"
            className="rounded border border-input bg-background px-2 py-1 font-mono text-xs"
            placeholder="cte_name"
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={bodyId} className="text-xs font-medium">
            Body (SELECT, VALUES, …)
          </label>
          <textarea
            id={bodyId}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="vqe-cte-add-body"
            className="min-h-[80px] resize-y rounded border border-input bg-background px-2 py-1 font-mono text-xs"
            placeholder="SELECT 1"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => { reset(); setOpen(false); }}
            data-testid="vqe-cte-add-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={name.trim().length === 0 || body.trim().length === 0}
            onClick={() => { void submit(); }}
            data-testid="vqe-cte-add-submit"
          >
            Add CTE
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
